<template>
	<v-chart
		class="chart"
		:option="option"
		ref="chart"
		:init-options="{ renderer: 'svg' }"
		autoresize
	/>
</template>




<script>
// https://github.com/ecomfe/vue-echarts?tab=readme-ov-file
import VChart from 'vue-echarts';
// import * as echarts from 'echarts'
import { useStationsStore } from '@/stores/StationsStore.js'
import { useStationsHistoryStore } from '@/stores/StationsHistoryStore.js'
import { INFLUXDB } from '@/api/api.js'
import { Station } from '@/models/station.js'
import { ServiceUnit } from '@/models/serviceUnit.js'
import {
	dayStart,
	dayEnd,
	addSecondsToDate,
} from '@/helpers.js'

import { use, graphic } from 'echarts/core'
import {
	CustomChart
} from 'echarts/charts'
import {
	TooltipComponent,
	TitleComponent,
	DataZoomComponent,
	GridComponent
} from 'echarts/components'
import {
	CanvasRenderer,
	SVGRenderer,
} from 'echarts/renderers'

use([
	TooltipComponent,
	TitleComponent,
	DataZoomComponent,
	GridComponent,
	CustomChart,
	CanvasRenderer,
	SVGRenderer,
])


function timeLabelFormatter(value) {
	const hours = Math.floor(value / 3600) // Кількість годин
	const minutes = Math.floor((value % 3600) / 60) // Кількість хвилин
	const seconds = Math.floor(value % 60) // Кількість секунд

	// Format as "hh:mm" if seconds == 0 and "hh:mm:ss" if not
	if (value % 60 == 0) {
		return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
	} else {
		return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
	}
}


/*
Render chart item as rectancle.
Start and stop depends on metric time. Clip rectangle if it going out of bounds.
Color depends of metric value.
*/
function renderItem(params, api) {
	const categoryId = api.value(1)
	const startTime = api.value(0)
	const duration = api.value(2)
	const color = api.value(3)

	const startPoint = api.coord([startTime, categoryId])
	const endPoint = api.coord([startTime + duration, categoryId])
	const height = api.size([0, 1])[1] * 0.6

	// Create rectangle
	const rect = {
		x: startPoint[0],
		y: startPoint[1] - height / 2,
		width: endPoint[0] - startPoint[0],
		height: height,
	};

	// Coordinates system boundaries (graph area)
	const coordSys = params.coordSys;

	// Clip rectangle if it going out of bounds.
	const clippedRect = graphic.clipRectByRect(rect, {
		x: coordSys.x,
		y: coordSys.y,
		width: coordSys.width,
		height: coordSys.height,
	});

	// If no rect
	if (!clippedRect) {
		return
	}

	return {
		type: 'rect',
		shape: clippedRect,
		style: {
			fill: color, // Rectangle color
			stroke: '#ffffff', // Border color
			lineWidth: 0.1, // Border width
			opacity: 0.8, // Border opacity
		},
	}
}

function cropDataToZoom(data, currentZoomStart, currentZoomEnd) {
	return data
		.map(item => {
			const start = item[0];
			const duration = item[2];
			const end = start + duration;
			
			// Якщо кінець до початку зуму — пропускаємо
			if (end <= currentZoomStart) return null;

			// Якщо початок після кінця зуму — пропускаємо
			if (start >= currentZoomEnd) return null;

			// Обрізаємо частину зліва
			const croppedStart = Math.max(start, currentZoomStart);
			const croppedEnd = Math.min(end, currentZoomEnd);

			return [
				croppedStart,
				item[1], // y (категорія)
				croppedEnd - croppedStart,
				item[3], // колір
				item[4],
			];
		})
		.filter(Boolean); // Видаляємо null
}


export default {
	WHITE: '#ffffff',
	GREEN: '#73bf69',
	YELLOW: '#ffee52',
	RED: '#f2495c',
	GREY: '#DBE2E9',
	// ORANGE: '#ff9830',
	BLUE: '#5794f2',

	TIME_DIVIDER: 1000,

	METRIC_NAME: 'стан',
	METRIC_INDEX: 0,
	CONNECTION_NAME: 'зв\'язок',
	CONNECTON_INDEX: 1,


	components: {
		VChart,
	},


	props: {
		stationId: {
			type: String,
			require: true,
		},
		metricName: {
			type: String,
			require: true,
		},
		date: {
			type: Date,
			require: true,
		},
	},


	emits: {},


	setup() {
		const stationsStore = useStationsStore()
		const historyStore = useStationsHistoryStore()

		return {
			stationsStore,
			historyStore,
			Station,
			INFLUXDB,
		}
	},


	data() {
		return {
			// Graph configuration
			option: {
				animation: false,
				
				title: {
					text: '',
					left: 'center'
				},
				tooltip: {
					trigger: 'item',
					formatter: (params) => {
						let statePromt = `невідомо`
						// Set state depend on metric
						if (params.componentIndex == this.$options.METRIC_INDEX) {
							statePromt = ServiceUnit.boxDoorStatePromt(params.value[4])
						} else if (params.componentIndex == this.$options.CONNECTON_INDEX) {
							statePromt = params.value[4] ? `є зв'язок` : `немає зв'язку`
						}
						
						return `
							<div>
								<b>Стан:</b> ${statePromt} <br>
								<b>Час фіксації:</b> ${timeLabelFormatter(params.value[0])} <br>
							</div>
						`
					}
				},
				dataZoom: [
					// Slider on bottom of the chart
					{
						type: 'slider',
						show: true,
						realtime: true,
						xAxisIndex: 0,
						bottom: 10,
						dataBackground: {
							areaStyle: {
								opacity: 0.1,
							}
						},
						labelFormatter: timeLabelFormatter
					},
					// For touchscreen
					{
						type: 'inside',
						disabled: false,
						realtime: true,
						xAxisIndex: 0,
					},
				],
				grid: {
					show: true,
					top: 30,
					bottom: 70,
					left: 60,
					right: 15,
					backgroundColor: this.$options.WHITE,
					borderWidth: 0
				},
				xAxis: {
					min: 0,
					max: 86400,
					interval: 3600,
					axisLabel: {
						formatter: timeLabelFormatter,
						hideOverlap: true, // prevent overlpping
					},
				},
				yAxis: {
					data: [this.$options.METRIC_NAME, this.$options.CONNECTION_NAME],
				},
				series: [
					{
						type: 'custom',
						renderItem: renderItem,
						data: [],
					},
					{
						type: 'custom',
						renderItem: renderItem,
						data: [],
					},
				]
			},

			rawConnection: [],
			rawMetric: [],
		}
	},

	watch: {
		// Update chart on date update
		async date(/*newDate*/) {
			if (this.date !== null) {
				// Fill preview
				this.fillChart()

				this.option.title.text = 'Завантаження...'

				// Show loading animation
				// this.chartInstance.showLoading() // don`t work for this chart

				await this.historyStore.loadMetrics(
					this.metricsId,
					[
						[INFLUXDB.station_metrics.service_unit, ServiceUnit.metricKeys.isConnect],
						[INFLUXDB.station_metrics.service_unit, this.metricName],
					],
					dayStart(this.date),
					dayEnd(this.date),
					false,
				)

				this.fillChart()

				this.option.title.text = this.dateText
			}
		},

		updateTriger() {
			this.fillChart()
		},
	},

	created() {},

	mounted() {
		if (this.date !== null) {
			this.option.title.text = this.dateText
			this.fillChart()
		}

		this.$nextTick(() => {
			// Add chart zoom handler
			this.chartInstance.on('dataZoom', (params) => {
				// Get axis depends on zoom type
				const axis = params.batch ? params.batch[0] : params
				const currentZoomStart = axis?.start ? 86400 / 100 * axis?.start : 0
				const currentZoomEnd = axis?.end ? 86400 / 100 * axis?.end : 86400

				this.option.series[this.$options.METRIC_INDEX].data = cropDataToZoom(this.rawMetric, currentZoomStart, currentZoomEnd)
				this.option.series[this.$options.CONNECTON_INDEX].data = cropDataToZoom(this.rawConnection, currentZoomStart, currentZoomEnd)
				// this.chartInstance.setOption(this.option, { notMerge: true });
			})
		})
	},

	methods: {
		fillChart() {
			// Clear previous data
			this.option.series[this.$options.METRIC_INDEX].data = []
			this.option.series[this.$options.CONNECTON_INDEX].data = []
			this.rawConnection = []
			this.rawMetric = []

			// Back data zoom to default state
			this.$refs.chart?.chart.dispatchAction({
				type: 'dataZoom',
				start: 0,
				end: 100
			})
			
			// Chart satart time (day begin time)
			const startTime = new Date(this.date)
			startTime.setHours(0, 0, 0, 0, 0)
			// Chart stop time
			const stopTime = new Date(this.date)
			stopTime.setHours(23, 59, 59, 999, 0)
			
			const prevDayCircuitLastMetric = this.historyStore.getMetricHistory(
				this.metricsId,
				INFLUXDB.station_metrics.service_unit,
				this.metricName,
				addSecondsToDate(dayStart(this.date), -60 * 60 * 6),
				addSecondsToDate(dayStart(this.date), -1),
			)?.at(-1)

			const circuitMetrics = this.historyStore.getMetricHistory(
				this.metricsId,
				INFLUXDB.station_metrics.service_unit,
				this.metricName,
				dayStart(this.date),
				dayEnd(this.date),
			)
			
			let drawStopTime = new Date(startTime.getTime())
			for (let i = 0; i < circuitMetrics.length; i++) {
				const metric = circuitMetrics[i]
				const nextMetric = circuitMetrics[i + 1]
				const metricDuraion = nextMetric ?
					Math.floor((nextMetric.time.getTime() - metric.time.getTime()) / this.$options.TIME_DIVIDER) :
					10

				let metricColor = this.$options.WHITE
				switch(parseInt(metric.value)) {
					case ServiceUnit.boxDoorStateCodes.open:
						metricColor = this.$options.RED
						break
					case ServiceUnit.boxDoorStateCodes.close:
						metricColor = this.$options.GREEN
						break
					case ServiceUnit.boxDoorStateCodes.short:
					metricColor = this.$options.YELLOW
						break
				}
				
				// If last metric
				if (!nextMetric) {
					// Add gray filling
					const greyMetricDuration = Math.floor((stopTime.getTime() - drawStopTime.getTime()) / this.$options.TIME_DIVIDER)
					this.rawMetric.push([
						Math.floor((drawStopTime.getTime() - startTime.getTime()) / this.$options.TIME_DIVIDER),
						this.$options.METRIC_NAME,
						greyMetricDuration,
						this.$options.GREY,
						3,
					])
				}
				drawStopTime = nextMetric ? new Date(nextMetric.time) : new Date(stopTime)

				// If first metric in day chart
				if (i == 0) {
					// Fill like last metric prev day
					let metricColor = this.$options.GREY
					switch(parseInt(prevDayCircuitLastMetric?.value)) {
						case ServiceUnit.boxDoorStateCodes.open:
							metricColor = this.$options.RED
							break
						case ServiceUnit.boxDoorStateCodes.close:
							metricColor = this.$options.GREEN
							break
						case ServiceUnit.boxDoorStateCodes.short:
						metricColor = this.$options.YELLOW
							break
					}
					this.rawMetric.push([
						0,
						this.$options.METRIC_NAME,
						Math.floor((metric.time.getTime() - startTime.getTime()) / this.$options.TIME_DIVIDER),
						metricColor,
						true,
					])
				}

				this.rawMetric.push([
					Math.floor((metric.time.getTime() - startTime.getTime()) / this.$options.TIME_DIVIDER),
					this.$options.METRIC_NAME,
					metricDuraion,
					metricColor,
					metric.value,
				])
			}

			const connectionMetrics = this.historyStore.getMetricHistory(
				this.metricsId,
				INFLUXDB.station_metrics.service_unit,
				ServiceUnit.metricKeys.isConnect,
				dayStart(this.date),
				dayEnd(this.date),
			)

			for (let i = 0; i < connectionMetrics.length; i++) {
				const metric = connectionMetrics[i]
				const nextMetric = connectionMetrics[i + 1]
				const metricDuraion = nextMetric ?
					Math.floor((nextMetric.time.getTime() - metric.time.getTime()) / this.$options.TIME_DIVIDER) :
					10

				// If last metric
				if (!nextMetric) {
					// Add gray filling
					const greyMetricDuration = Math.floor((stopTime.getTime() - metric.time.getTime()) / this.$options.TIME_DIVIDER)
					this.rawMetric.push([
						Math.floor((metric.time.getTime() - startTime.getTime()) / this.$options.TIME_DIVIDER),
						this.$options.CONNECTION_NAME,
						greyMetricDuration,
						this.$options.GREY,
						3,
					])
				}

				this.rawConnection.push([
					Math.floor((metric.time.getTime() - startTime.getTime()) / this.$options.TIME_DIVIDER),
					this.$options.CONNECTION_NAME,
					metricDuraion,
					metric.value == true && nextMetric?.value == true
						? this.$options.GREEN
						: this.$options.RED,
					metric.value,
				])
				// console.log('val', metric.value)
			}

			this.option.series[this.$options.CONNECTON_INDEX].data = [...this.rawConnection]
			this.option.series[this.$options.METRIC_INDEX].data = [...this.rawMetric]
			// console.log('Connection metrics', this.option.series[this.$options.CONNECTON_INDEX].data)
		},
	},


	computed: {
		station() { return this.stationsStore.station(this.stationId) },

		metricsId() { return this.station.passport.get(Station.passportKeys.metricsId) },

		chartInstance() { return this.$refs.chart?.chart },

		updateTriger() { return this.historyStore.updateTriger },

		dateText() {
			return `${String(this.date.getDate()).padStart(2, '0')}.${String(this.date.getMonth() + 1).padStart(2, '0')}.${this.date.getFullYear()}`
		},
	},
}
</script>




<style scoped>
</style>